import React, { useEffect, useRef } from 'react'

const parseColorToRgb = (value) => {
  if (!value || typeof value !== 'string') return null

  const color = value.trim().toLowerCase()

  if (color.startsWith('#')) {
    const hex = color.slice(1)
    if (hex.length === 3) {
      const r = parseInt(hex[0] + hex[0], 16)
      const g = parseInt(hex[1] + hex[1], 16)
      const b = parseInt(hex[2] + hex[2], 16)
      return { r, g, b }
    }
    if (hex.length === 6) {
      const r = parseInt(hex.slice(0, 2), 16)
      const g = parseInt(hex.slice(2, 4), 16)
      const b = parseInt(hex.slice(4, 6), 16)
      return { r, g, b }
    }
    return null
  }

  const rgbMatch = color.match(/rgba?\(([^)]+)\)/)
  if (!rgbMatch) return null

  const channels = rgbMatch[1]
    .split(',')
    .map((part) => Number.parseFloat(part.trim()))
    .filter((num) => Number.isFinite(num))

  if (channels.length < 3) return null

  return {
    r: Math.max(0, Math.min(255, Math.round(channels[0]))),
    g: Math.max(0, Math.min(255, Math.round(channels[1]))),
    b: Math.max(0, Math.min(255, Math.round(channels[2])))
  }
}

const getPerceptualLuminance = ({ r, g, b }) => (0.2126 * r) + (0.7152 * g) + (0.0722 * b)
const toRgba = ({ r, g, b }, alpha) => `rgba(${r}, ${g}, ${b}, ${alpha})`

// Convert any color string to rgba with alpha
const colorToRgba = (colorStr, alpha) => {
  const rgb = parseColorToRgb(colorStr)
  if (rgb) {
    return toRgba(rgb, alpha)
  }
  // Fallback: if already rgba or can't parse, return as-is with opacity
  return colorStr
}

// Easing function for smooth interpolation
const easeInOut = (t) => t < 0.5 ? 2 * t * t : -1 + (4 - 2 * t) * t

// Interpolate between keyframes
const interpolateKeyframes = (keyframes, normalizedTime) => {
  // Find surrounding keyframes
  let i = 0
  while (i < keyframes.length - 1 && keyframes[i + 1].time <= normalizedTime) {
    i++
  }

  const k1 = keyframes[i]
  const k2 = keyframes[(i + 1) % keyframes.length]

  // Handle wrap-around at end of animation
  let k2Time = k2.time
  if (k2Time <= k1.time) {
    k2Time = 1.0
  }

  // Interpolate
  const segmentDuration = k2Time - k1.time
  const segmentProgress = segmentDuration > 0 ? (normalizedTime - k1.time) / segmentDuration : 0
  const easedProgress = easeInOut(segmentProgress)

  return {
    x: k1.x + (k2.x - k1.x) * easedProgress,
    y: k1.y + (k2.y - k1.y) * easedProgress,
    scale: k1.scale + (k2.scale - k1.scale) * easedProgress,
    rotate: k1.rotate + (k2.rotate - k1.rotate) * easedProgress
  }
}

const CanvasAnimatedBackground = ({
  colors = null,
  brightness = 100,
  animationPaused = false
}) => {
  const canvasRef = useRef(null)
  const animationRef = useRef(null)
  const blobsRef = useRef([])
  const startTimeRef = useRef(Date.now())
  const pausedTimeRef = useRef(0)
  const totalPausedDurationRef = useRef(0)
  const gradientColorsRef = useRef(null)
  const darkestColorRef = useRef(null)

  useEffect(() => {
    const canvas = canvasRef.current
    if (!canvas) return

    const ctx = canvas.getContext('2d', {
      alpha: false,
      desynchronized: true // Better performance
    })

    // Default colors
    const defaultGradientColors = ['#486fcb', '#7ea6df', '#f5a0b9', '#fbb164', '#f15050']
    const defaultBlobColors = ['#e51e25', '#e51e25']

    // Initialize color refs if not set
    if (!gradientColorsRef.current) {
      const gradientColors = colors?.gradientColors || defaultGradientColors
      const blobColors = colors?.blobColors || defaultBlobColors

      // Calculate darkest color for ambient dark backgrounds
      const parsedPaletteColors = [...gradientColors, ...blobColors]
        .map(parseColorToRgb)
        .filter(Boolean)
      const darkestColor = parsedPaletteColors.length
        ? parsedPaletteColors.reduce((darkest, current) => (
          getPerceptualLuminance(current) < getPerceptualLuminance(darkest) ? current : darkest
        ))
        : { r: 11, g: 15, b: 27 }

      gradientColorsRef.current = gradientColors
      darkestColorRef.current = darkestColor
    }

    // Initialize blobs with animation parameters (ported from CSS) - only if not already initialized
    const initBlobs = () => {
      const defaultGradientColors = ['#486fcb', '#7ea6df', '#f5a0b9', '#fbb164', '#f15050']
      const defaultBlobColors = ['#e51e25', '#e51e25']
      const gradientColors = gradientColorsRef.current || defaultGradientColors
      const blobColors = colors?.blobColors || defaultBlobColors

      return [
      {
        // Blob 1
        color: blobColors[0] || gradientColors[0],
        baseX: 28, baseY: 24,
        size: 56,
        roamKeyframes: [
          { x: -32, y: -18, scale: 1.0, rotate: 0, time: 0 },
          { x: 20, y: -26, scale: 0.92, rotate: 80, time: 0.2 },
          { x: 32, y: 12, scale: 1.06, rotate: 154, time: 0.45 },
          { x: -10, y: 30, scale: 0.95, rotate: 225, time: 0.7 },
          { x: -32, y: -18, scale: 1.0, rotate: 360, time: 1.0 }
        ],
        roamDuration: 20
      },
      {
        // Blob 2
        color: blobColors[1] || gradientColors[4],
        baseX: 58, baseY: 18,
        size: 48,
        roamKeyframes: [
          { x: 30, y: -18, scale: 1.04, rotate: 0, time: 0 },
          { x: -10, y: -30, scale: 0.9, rotate: -80, time: 0.22 },
          { x: -34, y: 6, scale: 1.07, rotate: -170, time: 0.5 },
          { x: 8, y: 28, scale: 0.96, rotate: -250, time: 0.76 },
          { x: 30, y: -18, scale: 1.04, rotate: -360, time: 1.0 }
        ],
        roamDuration: 24
      },
      {
        // Blob 3
        color: gradientColors[2],
        baseX: 16, baseY: 44,
        size: 64,
        roamKeyframes: [
          { x: -24, y: 26, scale: 1.0, rotate: 0, time: 0 },
          { x: 12, y: 32, scale: 1.1, rotate: 75, time: 0.2 },
          { x: 34, y: -6, scale: 0.96, rotate: 148, time: 0.48 },
          { x: -10, y: -30, scale: 1.06, rotate: 224, time: 0.74 },
          { x: -24, y: 26, scale: 1.0, rotate: 360, time: 1.0 }
        ],
        roamDuration: 22
      },
      {
        // Blob 4
        color: gradientColors[0],
        baseX: 50, baseY: 56,
        size: 40,
        roamKeyframes: [
          { x: 22, y: 22, scale: 0.92, rotate: 0, time: 0 },
          { x: -14, y: 30, scale: 1.04, rotate: -92, time: 0.24 },
          { x: -34, y: -8, scale: 0.96, rotate: -176, time: 0.5 },
          { x: 2, y: -32, scale: 1.08, rotate: -268, time: 0.78 },
          { x: 22, y: 22, scale: 0.92, rotate: -360, time: 1.0 }
        ],
        roamDuration: 18
      },
      {
        // Blob 5
        color: gradientColors[4],
        baseX: 62, baseY: 46,
        size: 52,
        roamKeyframes: [
          { x: 34, y: 10, scale: 0.98, rotate: 0, time: 0 },
          { x: 12, y: -28, scale: 1.05, rotate: 74, time: 0.18 },
          { x: -14, y: -30, scale: 0.92, rotate: 150, time: 0.42 },
          { x: -36, y: 8, scale: 1.08, rotate: 240, time: 0.7 },
          { x: 34, y: 10, scale: 0.98, rotate: 360, time: 1.0 }
        ],
        roamDuration: 26
      }
    ]}

    // Only initialize blobs once
    if (blobsRef.current.length === 0) {
      blobsRef.current = initBlobs()
    }

    // Canvas resize handler
    const resizeCanvas = () => {
      const dpr = window.devicePixelRatio || 1
      canvas.width = window.innerWidth * dpr
      canvas.height = window.innerHeight * dpr
      canvas.style.width = `${window.innerWidth}px`
      canvas.style.height = `${window.innerHeight}px`
      ctx.scale(dpr, dpr)
    }

    window.addEventListener('resize', resizeCanvas)
    resizeCanvas()

    // Draw base gradient (replaces .organic-bg-base)
    const drawBaseGradient = () => {
      const w = window.innerWidth
      const h = window.innerHeight
      const defaultGradientColors = ['#486fcb', '#7ea6df', '#f5a0b9', '#fbb164', '#f15050']
      const gradientColors = gradientColorsRef.current || defaultGradientColors
      const darkestColor = darkestColorRef.current || { r: 11, g: 15, b: 27 }

      // Dark base gradient
      const baseGrad = ctx.createRadialGradient(
        w * 0.5, h * 0.5, 0,
        w * 0.5, h * 0.5, Math.max(w, h) * 0.7
      )
      baseGrad.addColorStop(0, toRgba(darkestColor, 0.74))
      baseGrad.addColorStop(1, toRgba(darkestColor, 0.96))

      ctx.fillStyle = baseGrad
      ctx.fillRect(0, 0, w, h)

      // Ambient gradient 1: top-left
      const grad1 = ctx.createRadialGradient(
        w * 0.18, h * 0.22, 0,
        w * 0.18, h * 0.22, Math.max(w, h) * 0.64
      )
      grad1.addColorStop(0, gradientColors[1] || defaultGradientColors[1])
      grad1.addColorStop(1, 'transparent')

      ctx.save()
      ctx.globalAlpha = 0.3
      ctx.fillStyle = grad1
      ctx.fillRect(0, 0, w, h)
      ctx.restore()

      // Ambient gradient 2: top-right
      const grad2 = ctx.createRadialGradient(
        w * 0.82, h * 0.04, 0,
        w * 0.82, h * 0.04, Math.max(w, h) * 0.58
      )
      grad2.addColorStop(0, gradientColors[3] || defaultGradientColors[3])
      grad2.addColorStop(1, 'transparent')

      ctx.save()
      ctx.globalAlpha = 0.25
      ctx.fillStyle = grad2
      ctx.fillRect(0, 0, w, h)
      ctx.restore()
    }

    // Draw blob with radial gradient
    const drawBlob = (blob, time) => {
      const w = window.innerWidth
      const h = window.innerHeight
      const vmax = Math.max(w, h)
      const vw = w / 100
      const vh = h / 100

      // Calculate animation progress (0-1, looping)
      const roamProgress = (time / blob.roamDuration) % 1
      const pos = interpolateKeyframes(blob.roamKeyframes, roamProgress)

      // Calculate final position
      const centerX = (blob.baseX * vw) + (pos.x * vw)
      const centerY = (blob.baseY * vh) + (pos.y * vh)
      const radius = (blob.size / 100) * vmax * pos.scale

      // Create radial gradient with blur effect simulation
      const gradient = ctx.createRadialGradient(
        centerX, centerY, 0,
        centerX, centerY, radius
      )

      const color = blob.color
      // Simulate blur with gradient alpha falloff
      gradient.addColorStop(0, colorToRgba(color, 1.0))
      gradient.addColorStop(0.3, colorToRgba(color, 1.0))
      gradient.addColorStop(0.6, colorToRgba(color, 0.67)) // ~66% alpha
      gradient.addColorStop(0.8, colorToRgba(color, 0.27)) // ~25% alpha
      gradient.addColorStop(1, colorToRgba(color, 0)) // Transparent

      // Draw
      ctx.save()
      ctx.globalCompositeOperation = 'screen' // Blend mode
      ctx.globalAlpha = 0.72

      ctx.fillStyle = gradient
      ctx.beginPath()
      ctx.arc(centerX, centerY, radius * 1.5, 0, Math.PI * 2) // *1.5 to extend blur radius
      ctx.fill()

      ctx.restore()
    }

    // Draw vignette (replaces .organic-bg-vignette)
    const drawVignette = () => {
      const w = window.innerWidth
      const h = window.innerHeight

      // Top highlight - centered at 28% from top
      const topGrad = ctx.createRadialGradient(
        w * 0.5, h * 0.28, 0,
        w * 0.5, h * 0.28, h * 0.5
      )
      topGrad.addColorStop(0, 'rgba(255, 255, 255, 0.14)')
      topGrad.addColorStop(0.5, 'transparent') // Fade to transparent at 50%

      ctx.save()
      ctx.fillStyle = topGrad
      ctx.fillRect(0, 0, w, h)
      ctx.restore()

      // Bottom darkening - centered at bottom, extends to farthest corner
      // Calculate distance to farthest corner (top corners)
      const bottomGradRadius = Math.sqrt((w * 0.5) ** 2 + h ** 2)
      const bottomGrad = ctx.createRadialGradient(
        w * 0.5, h, 0,
        w * 0.5, h, bottomGradRadius
      )
      bottomGrad.addColorStop(0, 'rgba(0, 0, 0, 0.28)')
      bottomGrad.addColorStop(0.84, 'rgba(0, 0, 0, 0.52)')
      // No transparent stop - maintains 0.52 alpha to edge

      ctx.save()
      ctx.fillStyle = bottomGrad
      ctx.fillRect(0, 0, w, h)
      ctx.restore()
    }

    // Animation loop
    const animate = () => {
      const currentTime = Date.now()
      const time = animationPaused
        ? pausedTimeRef.current - totalPausedDurationRef.current
        : currentTime - startTimeRef.current - totalPausedDurationRef.current

      // Clear canvas
      ctx.clearRect(0, 0, canvas.width, canvas.height)

      // Draw layers
      drawBaseGradient()

      // Draw all blobs
      blobsRef.current.forEach(blob => {
        drawBlob(blob, time / 1000) // Convert to seconds
      })

      drawVignette()

      animationRef.current = requestAnimationFrame(animate)
    }

    animate()

    // Cleanup
    return () => {
      if (animationRef.current) {
        cancelAnimationFrame(animationRef.current)
      }
      window.removeEventListener('resize', resizeCanvas)
    }
  }, [animationPaused]) // Removed 'colors' to prevent animation reset on color change

  // Handle animation pause state
  useEffect(() => {
    if (animationPaused) {
      pausedTimeRef.current = Date.now()
    } else {
      if (pausedTimeRef.current > 0) {
        totalPausedDurationRef.current += Date.now() - pausedTimeRef.current
      }
    }
  }, [animationPaused])

  // Update blob colors when colors prop changes
  useEffect(() => {
    if (colors) {
      const defaultGradientColors = ['#486fcb', '#7ea6df', '#f5a0b9', '#fbb164', '#f15050']
      const defaultBlobColors = ['#e51e25', '#e51e25']

      const newGradientColors = colors.gradientColors || defaultGradientColors
      const newBlobColors = colors.blobColors || defaultBlobColors

      // Update gradient color refs
      gradientColorsRef.current = newGradientColors

      // Calculate and update darkest color
      const parsedPaletteColors = [...newGradientColors, ...newBlobColors]
        .map(parseColorToRgb)
        .filter(Boolean)
      const darkestColor = parsedPaletteColors.length
        ? parsedPaletteColors.reduce((darkest, current) => (
          getPerceptualLuminance(current) < getPerceptualLuminance(darkest) ? current : darkest
        ))
        : { r: 11, g: 15, b: 27 }
      darkestColorRef.current = darkestColor

      // Update blob colors
      if (blobsRef.current.length > 0) {
        blobsRef.current[0].color = newBlobColors[0] || newGradientColors[0]
        blobsRef.current[1].color = newBlobColors[1] || newGradientColors[4]
        blobsRef.current[2].color = newGradientColors[2]
        blobsRef.current[3].color = newGradientColors[0]
        blobsRef.current[4].color = newGradientColors[4]
      }
    }
  }, [colors])

  return (
    <canvas
      ref={canvasRef}
      className="fixed inset-0 z-0 pointer-events-none"
      style={{ opacity: brightness / 100 }}
    />
  )
}

export default CanvasAnimatedBackground
